import React from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Form } from "react-final-form";
import { Dialog } from "@syos-music/common";
import { SliderField } from "@syos-music/form";
import { Instrument, InstrumentType } from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import { Button, CircularProgress, Divider, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import MouthpieceLabel from "components/mouthpiece/MouthpieceLabel";

const { OriginalsType } = Product.displayModes.originalsMouthpiece;

const QUERY = gql`
  query (
    $type: String!
    # Originals
    $instrument: String
    $instrumentType: String
    $originalsType: String
    # Signature
    $signatureMouthpiece: ID
  ) {
    soundTags {
      id
      instruments
      label {
        fr
      }
    }

    entityTags(
      type: $type
      # Originals
      instrument: $instrument
      instrumentType: $instrumentType
      originalsType: $originalsType
      # Signature
      signatureMouthpiece: $signatureMouthpiece
    ) {
      tags {
        tag {
          id
        }
        notation
      }
    }
  }
`;

const MUTATION = gql`
  mutation (
    $type: String!
    # Originals
    $instrument: String
    $instrumentType: String
    $originalsType: String
    # Signature
    $signatureMouthpiece: ID
    $tags: [EntityTagsNotationInput]!
  ) {
    UpsertEntityTags(
      type: $type
      # Originals
      instrument: $instrument
      instrumentType: $instrumentType
      originalsType: $originalsType
      # Signature
      signatureMouthpiece: $signatureMouthpiece

      tags: $tags
    ) {
      entityTags {
        id
      }
    }
  }
`;

export default function EntityTagsFormDialog({ entityMatch, ...props }) {
  const variables = React.useMemo(() => {
    if (entityMatch?.type === "originals") {
      return {
        type: entityMatch.type,
        instrument: entityMatch.instrument,
        instrumentType: entityMatch.instrumentType,
        originalsType: entityMatch.originalsType,
      };
    }

    if (entityMatch?.type === "signature") {
      return {
        type: entityMatch.type,
        signatureMouthpiece: entityMatch.signatureMouthpiece.id,
      };
    }
  }, [entityMatch]);

  const { data, loading } = useQuery(QUERY, {
    variables,
    skip: !variables,
    fetchPolicy: "network-only",
  });

  const instrument =
    entityMatch?.instrument ??
    entityMatch?.signatureMouthpiece.mouthpieceRef?.instrument;

  const initialValues = React.useMemo(() => {
    if (!instrument) return {};

    return {
      notations: data?.soundTags
        .filter((tag) => tag.instruments.includes(instrument))
        .reduce(
          (acc, tag) =>
            Object.assign(acc, {
              [tag.id]:
                data.entityTags?.tags.find(
                  (tagNotation) => tagNotation.tag.id === tag.id
                )?.notation ?? 0,
            }),
          {}
        ),
    };
  }, [data, instrument]);

  const [upsertEntityTags] = useMutation(MUTATION);

  const handleSubmit = React.useCallback(
    (values) => {
      return upsertEntityTags({
        variables: {
          ...variables,

          tags: Object.entries(values.notations).map(([tag, notation]) => ({
            tag,
            notation,
          })),
        },
      })
        .then(() => {
          props.onClose?.();
        })
        .catch(() => true);
    },
    [variables, upsertEntityTags, props]
  );

  return (
    <Dialog open={Boolean(entityMatch)} {...props}>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            {entityMatch?.type === "originals" && (
              <Dialog.Title>
                <Stack direction="row" spacing="1ch">
                  <Instrument instrument={entityMatch.instrument} />{" "}
                  <InstrumentType
                    instrument={entityMatch.instrument}
                    instrumentType={entityMatch.instrumentType}
                  />
                  <OriginalsType originalsType={entityMatch.originalsType} />
                </Stack>
              </Dialog.Title>
            )}

            {entityMatch?.type === "signature" && (
              <Dialog.Title>
                <div>{entityMatch.signatureMouthpiece.signature}</div>
                <MouthpieceLabel
                  mouthpieceRef={entityMatch.signatureMouthpiece.mouthpieceRef}
                />
              </Dialog.Title>
            )}

            <Dialog.Content>
              {loading && <CircularProgress />}

              {data?.soundTags
                .filter((tag) => tag.instruments.includes(instrument))
                .map(({ id, label }) => (
                  <React.Fragment key={id}>
                    <SliderField
                      name={`notations.${id}`}
                      label={label.fr}
                      min={-1}
                      max={1}
                      step={0.01}
                    />
                    <Divider sx={{ m: (t) => t.spacing(1, -1) }} />
                  </React.Fragment>
                ))}
            </Dialog.Content>

            <Dialog.Actions>
              {props.onClose && (
                <Button onClick={props.onClose} variant="text">
                  Annuler
                </Button>
              )}

              <LoadingButton type="submit" loading={submitting}>
                Valider
              </LoadingButton>
            </Dialog.Actions>
          </form>
        )}
      </Form>
    </Dialog>
  );
}

EntityTagsFormDialog.fragments = {
  signatureMouthpiece: gql`
    fragment EntityTagsFormDialog_signatureMouthpiece on SignatureMouthpiece {
      id
      signature
      mouthpieceRef {
        instrument
        instrumentType

        ...MouthpieceLabel_mouthpieceRef
      }
    }

    ${MouthpieceLabel.fragments.mouthpieceRef}
  `,
};
